import React, { FC, useCallback, useLayoutEffect, useRef } from 'react'
import format from 'date-fns/format'
import styles from 'styles/components/card.module.scss'
import typography from 'styles/components/typography.module.scss'
import text from 'styles/utility/text.module.scss'
import { FlexRow } from 'components/Flex'
import { Spacer } from 'components/Utility'
import { SmallLabel } from 'components/Label'
import { BlackTag, BreadPreorderTag, GreyTag, LightTag } from 'components/Tag'
import { BlueButton } from 'components/Button'
import { FirebaseTimestampType } from 'firebase/index'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { initBreads, setPrevEditedBread } from 'reducks/bread/operations'
import { initSchedules } from 'reducks/schedule/operations'
import { initStocks } from 'reducks/stock/operations'
import { StockType } from 'types/stock'
import { initStoreDeliveryFees } from 'reducks/storeDeliveryFee/operations'
import { State } from 'index'
import { getPrevEditedBread } from 'reducks/bread/selectors'

type Props = {
  breadId: string
  breadName: string
  categoryName: string
  isTypeDaily: boolean
  isTypePreorder: boolean
  isTypeDelivery: boolean | undefined
  preorderStocks: StockType[]
  imageUrl: string
  status: string
  updatedAt: FirebaseTimestampType
}

const BreadCard: FC<Props> = React.memo((props) => {
  const {
    breadId,
    breadName,
    categoryName,
    isTypeDaily,
    isTypePreorder,
    isTypeDelivery,
    preorderStocks,
    imageUrl,
    status,
    updatedAt,
  } = props

  const dispatch = useDispatch()
  const prevEditedBread = useSelector((state: State) => getPrevEditedBread(state))
  const ref = useRef<HTMLDivElement>(null)

  const goToEditPage = useCallback(() => {
    dispatch(initBreads())
    dispatch(initSchedules())
    dispatch(initStocks())
    dispatch(initStoreDeliveryFees())
    dispatch(setPrevEditedBread(breadId))
    dispatch(push(`/product/edit/${breadId}`))
  }, [dispatch, breadId])

  const disabledStyle = status === 'private' ? styles.row__disabled : ''

  // 前回編集した商品位置まで自動スクロールする
  useLayoutEffect(() => {
    if (prevEditedBread === '' || prevEditedBread !== breadId) return
    setTimeout(() => {
      const clientRect = ref?.current?.getBoundingClientRect()
      const clientRectTop = clientRect?.top || 0
      window.scrollTo({
        behavior: 'smooth',
        top: clientRectTop - 95,
      })
    }, 1000)
  }, [prevEditedBread, breadId, ref])

  return (
    <div className={styles.row__inner} ref={ref}>
      <FlexRow justifyContent={'start'} alignItems={'start'}>
        <div className={`${styles.row__image} ${disabledStyle}`}>
          <img src={imageUrl} alt={breadName} />
        </div>
        <Spacer size={12} />
        <div className={styles.row__body}>
          <FlexRow justifyContent={'between'} alignItems={'start'}>
            <div>
              {status === 'private' && (
                <>
                  <BlackTag tagName={'販売停止中'} />
                  <Spacer size={12} />
                </>
              )}
              <h4 className={`${typography.headline_small} ${disabledStyle}`}>{breadName}</h4>
            </div>
            <Spacer size={12} />
            <BlueButton label={'編集'} onClick={goToEditPage} size={'xxsmall'} type={'outlined'} />
          </FlexRow>
          <Spacer size={8} />
          <div className={disabledStyle}>
            <FlexRow justifyContent={'start'}>
              <SmallLabel label={'カテゴリー ： '} />
              <Spacer size={4} />
              <GreyTag shape={'ellipse'} tagName={categoryName} />
            </FlexRow>
            <Spacer size={4} />
            <FlexRow alignItems={'baseline'} justifyContent={'start'}>
              <div className={styles.label}>
                <SmallLabel label={'注文タイプ ： '} />
              </div>
              <div className={styles.tag}>
                {isTypeDaily && (
                  <div className={styles.tag__item}>
                    <LightTag color={'blue'} label={'当日'} />
                  </div>
                )}
                {isTypePreorder && (
                  <div className={styles.tag__item}>
                    <BreadPreorderTag isConfigured={preorderStocks.length > 0} />
                  </div>
                )}
                {isTypeDelivery && (
                  <div className={styles.tag__item}>
                    <LightTag color={'orange'} label={'取り寄せ'} />
                  </div>
                )}
              </div>
            </FlexRow>
            <Spacer size={4} />
            <FlexRow justifyContent={'start'}>
              <SmallLabel label={'最終更新日 ： '} />
              <Spacer size={2} />
              <span className={text.font_xxsmall}>{format(updatedAt.toDate(), 'yyyy年MM月dd日 HH:mm')}</span>
            </FlexRow>
          </div>
        </div>
      </FlexRow>
    </div>
  )
})

export default BreadCard
